import { memo, useCallback, useEffect, useState } from "react";

import { fontFamilySelectOptionsMap } from "@hl/communities-app/lib/styles/global/fonts";
import { fontFamilyMap } from "@hl/communities-app/src/styles/global/fonts";
import { Select, ColorInput } from "@mantine/core";
import upperFirst from "lodash/upperFirst";

import {
  BackgroundType,
  CornersShape,
  EventType,
} from "apollo/graphql.generated";
import { ReactComponent as RoundedIcon } from "assets/icons/rounded-corner.svg";
import { ReactComponent as SquareIcon } from "assets/icons/square-corner.svg";
import useAnalytics from "hooks/useAnalytics";

import { Page, setTheme } from "../../ManagePage/state";

import * as styles from "./styles";

export type ThemeProps = {
  page: Page;
  onChange: () => void;
};

const getOptions = (values: string[]) =>
  values.map((value) => ({
    value,
    label: upperFirst(value.toLowerCase()),
  }));

const ThemeTab = ({ page, onChange }: ThemeProps) => {
  const { trackEvent } = useAnalytics();
  const [edited, setEdited] = useState<boolean>(false);
  const [backgroundType, setBackgroundType] = useState<string>(
    page.theme.backgroundType
  );
  const [backgroundColor, setBackgroundColor] = useState<string>(
    page.theme.backgroundColor
  );
  const [backgroundBottomColor, setBackgroundBottomColor] = useState<string>(
    page.theme.backgroundBottomColor ?? page.theme.backgroundColor
  );
  const [textColor, setTextColor] = useState<string>(page.theme.textColor);
  const [titleFont, setTitleFont] = useState<string>(page.theme.titleFont);
  const [bodyFont, setBodyFont] = useState<string>(page.theme.bodyFont);
  const [cornerShape, setCornerShape] = useState<string>(
    page.theme.cornerShape
  );

  useEffect(() => {
    setTheme({
      backgroundType,
      backgroundColor,
      backgroundBottomColor,
      textColor,
      titleFont,
      bodyFont,
      cornerShape,
    });
  }, [
    backgroundType,
    backgroundColor,
    backgroundBottomColor,
    textColor,
    titleFont,
    bodyFont,
    cornerShape,
  ]);

  // TODO: more granular, bundle?
  useEffect(() => {
    if (edited) {
      trackEvent(EventType.CreateGateEditor_EditTheme, {});
    }
  }, [edited]);

  const backgroundTypes = getOptions(Object.values(BackgroundType));
  const cornerShapes = getOptions(Object.values(CornersShape));

  const setChange = useCallback(() => {
    onChange();
    setEdited(true);
  }, [onChange]);

  const getCornerShapeIcon = useCallback(() => {
    switch (cornerShape.toLowerCase()) {
      case CornersShape.Rounded.toLowerCase():
        return <RoundedIcon />;
      case CornersShape.Square.toLowerCase():
        return <SquareIcon />;
    }
  }, [cornerShape]);

  return (
    <div css={styles.container}>
      <Select
        label="Background Type"
        defaultValue={backgroundType}
        data={backgroundTypes}
        onChange={(value) => {
          if (!value) return;
          setBackgroundType(value);
          setChange();
        }}
      />
      {backgroundType === BackgroundType.Gradient ? (
        <div css={styles.rowContainer}>
          <ColorInput
            label="Background Top Color"
            defaultValue={backgroundColor}
            onChange={(value) => {
              setBackgroundColor(value);
              setChange();
            }}
          />
          <ColorInput
            label="Background Bottom Color"
            defaultValue={backgroundBottomColor}
            onChange={(value) => {
              setBackgroundBottomColor(value);
              setChange();
            }}
          />
        </div>
      ) : (
        <ColorInput
          label="Background Color"
          defaultValue={backgroundColor}
          onChange={(value) => {
            setBackgroundColor(value);
            setChange();
          }}
        />
      )}
      <ColorInput
        label="Text Color"
        defaultValue={textColor}
        onChange={(value) => {
          setTextColor(value);
          setChange();
        }}
      />
      <Select
        label="Title Font"
        sx={{
          fontFamily: fontFamilyMap[titleFont],
        }}
        defaultValue={titleFont}
        data={fontFamilySelectOptionsMap}
        onChange={(value) => {
          if (!value) return;
          setTitleFont(value);
          setChange();
        }}
      />
      <Select
        label="Body Font"
        sx={{
          fontFamily: fontFamilyMap[bodyFont],
        }}
        defaultValue={bodyFont}
        data={fontFamilySelectOptionsMap}
        onChange={(value) => {
          if (!value) return;
          setBodyFont(value);
          setChange();
        }}
      />
      <Select
        label="Corner Shape"
        defaultValue={cornerShape}
        data={cornerShapes}
        icon={getCornerShapeIcon()}
        onChange={(value) => {
          if (!value) return;
          setCornerShape(value);
          setChange();
        }}
      />
    </div>
  );
};

export default memo(ThemeTab);
